import api from '@/api/axios';
import type { AxiosResponse } from 'axios';
import {
  DifficultyLevel,
  TemplateType,
  Template,
  TemplateItem,
} from '@/components/Template/types';
import { SetItem } from '@/stores/store.types';

type AssignTemplatePayload = {
  appDisplayName: string;
  name: string;
  patientIds: string[];
};

type FetchTemplatesParams = {
  page?: number;
  limit?: number;
  setType?: TemplateType | `${TemplateType},${TemplateType}`;
  difficulty?:
    | DifficultyLevel
    | `${DifficultyLevel},${DifficultyLevel}`
    | `${DifficultyLevel},${DifficultyLevel},${DifficultyLevel}`;
  name?: string;
  searchTerm?: string;
};

type FetchTemplatesResponse = {
  total: number;
  page: number;
  pageSize: number;
  templates: Template[];
};

type UpdateTemplate = {
  message: string;
  template: Template;
};

type CreateTemplatePayload = {
  name: string;
  description?: string;
  difficulty: DifficultyLevel;
  setType: 'Word' | 'Sentence';
  items: TemplateItem[];
};

export const isWordItem = (
  item: TemplateItem,
): item is TemplateItem & { word: SetItem['word'] } => {
  return 'word' in item;
};

export const assignTemplate = async ({
  payload,
  templateId,
}: {
  payload: AssignTemplatePayload;
  templateId: string;
}): Promise<AxiosResponse> => {
  try {
    return await api.post(`/api/templates/${templateId}/assign`, payload);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const fetchTemplates = async (
  params: FetchTemplatesParams = {},
): Promise<FetchTemplatesResponse> => {
  try {
    const { data }: AxiosResponse<FetchTemplatesResponse> = await api.get(
      '/api/templates',
      { params },
    );
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createTemplateFromSet = async (
  payload: CreateTemplatePayload,
): Promise<Template> => {
  try {
    const { data }: AxiosResponse<Template> = await api.post(
      `/api/templates`,
      payload,
    );
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteTemplateById = async (
  templateId: string,
): Promise<AxiosResponse> => {
  try {
    return await api.delete(`/api/templates/${templateId}`);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getTemplate = async (templateId: string): Promise<Template> => {
  try {
    const { data } = await api.get(`/api/templates/${templateId}`);
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateTemplate = async (
  templateId: string,
  payload: Partial<Template>,
): Promise<UpdateTemplate> => {
  try {
    return await api.put(`/api/templates/${templateId}`, payload);
  } catch (error) {
    return Promise.reject(error);
  }
};
